<template>
  <div id="AboutPage" class="h-screen w-screen flex flex-col overflow-hidden relative about-bg"
    :class="{ 'xl:h-auto xl:w-auto xl:overflow-visible': isDesktop }"
    style="font-family: 'Lato', sans-serif;">

    <!-- Decorative background (mobile only) -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none xl:hidden">
      <div class="absolute -top-20 -right-8 w-56 h-[380px] rotate-[20deg] rounded-[36px] about-deco-top"></div>
      <div class="absolute -bottom-16 -left-12 w-72 h-72 rounded-full about-deco-bottom"></div>
    </div>

    <div class="flex flex-col flex-1 min-h-0 relative z-10" :class="{ 'xl:flex-none': isDesktop }">

      <!-- Header: back + logo (mobile only) -->
      <div v-if="!isDesktop" class="pt-safe flex items-center gap-3 px-safe pb-3 about-header" :class="{ scrolled: isScrolled }">
        <button @click="backTo({ name: 'Profile' })"
          class="w-10 h-10 rounded-xl flex items-center justify-center bg-card about-back-btn active:scale-95 transition-transform duration-150 shrink-0">
          <ArrowLeftIcon class="h-[18px] w-[18px] text-foreground" />
        </button>
        <p class="text-[16px] font-bold text-foreground">{{ $t('profile.about.headline') }}</p>
      </div>

      <!-- Scrollable content -->
      <div class="flex-1 min-h-0 overflow-y-auto pb-safe px-safe-flush about-scroll" :class="{ 'xl:flex-none xl:overflow-visible xl:pb-0 xl:px-0': isDesktop }" @scroll="onScroll">

        <!-- App info section -->
        <div class="px-5 mb-4 mt-6">
          <div class="flex items-center gap-2 mb-2 pl-1">
            <div class="w-1 h-3.5 rounded-full" style="background: linear-gradient(180deg, #06C8FF, #009FE3);"></div>
            <h2 class="text-[13px] font-semibold text-muted-foreground">
              {{ $t('profile.about.headline') }}
            </h2>
          </div>
          <div class="about-card rounded-xl overflow-hidden">
            <div class="flex items-center justify-between px-4 py-3.5 about-info-divider cursor-pointer select-none"
              @click="onVersionTap">
              <span class="text-[13px] text-muted-foreground">{{ $t('profile.about.version') }}</span>
              <span class="text-[13px] font-semibold text-foreground">{{ version }}</span>
            </div>
            <div class="flex items-center justify-between px-4 py-3.5">
              <span class="text-[13px] text-muted-foreground">{{ $t('profile.about.platform') }}</span>
              <span class="text-[13px] font-semibold text-foreground">{{ platform }}</span>
            </div>
          </div>
        </div>

        <!-- Troubleshooting section -->
        <div class="px-5 mb-4">
          <div class="flex items-center gap-2 mb-2 pl-1">
            <div class="w-1 h-3.5 rounded-full" style="background: linear-gradient(180deg, #06C8FF, #009FE3);"></div>
            <h2 class="text-[13px] font-semibold text-muted-foreground">
              {{ $t('profile.about.troubleshooting') }}
            </h2>
          </div>

          <!-- Force reconnection card -->
          <div class="about-card rounded-xl overflow-hidden px-4 py-4 mb-3">
            <p class="text-[12px] text-muted-foreground leading-relaxed mb-4">
              {{ $t('profile.about.force_reconnect_description') }}
            </p>
            <button @click="forceReconnect"
              class="about-reconnect-btn flex items-center justify-center gap-2 w-full h-[44px] font-bold text-[14px] rounded-xl active:scale-[0.97] transition-all duration-150">
              <ArrowPathIcon class="h-4 w-4" />
              {{ $t('profile.about.force_reconnect') }}
            </button>
          </div>

          <!-- Clear cache card -->
          <div class="about-card rounded-xl overflow-hidden px-4 py-4">
            <p class="text-[12px] text-muted-foreground leading-relaxed mb-4">
              {{ $t('profile.about.clear_cache_description') }}
            </p>
            <button @click="clearCache"
              class="about-cache-btn flex items-center justify-center gap-2 w-full h-[44px] font-bold text-[14px] rounded-xl active:scale-[0.97] transition-all duration-150">
              <TrashIcon class="h-4 w-4" />
              {{ $t('profile.about.clear_cache') }}
            </button>
          </div>
        </div>

        <!-- Copyright footer -->
        <div class="flex justify-center pt-4 pb-6">
          <span class="text-[11px] text-muted-foreground/60">Apex Timing &copy; {{ currentYear }}</span>
        </div>

      </div>
    </div>
    <!-- Confirm dialog -->
    <BaseDialog :isOpen="confirmDialog.open" :sending="false"
      :confirmText="$t('dialogs.confirm')" :closeText="$t('dialogs.close')"
      :color="confirmDialog.color"
      @close="confirmDialog.open = false" @confirm="onConfirm">
      <DialogTitle as="h3" class="text-[16px] font-bold text-foreground mb-2">
        {{ confirmDialog.title }}
      </DialogTitle>
      <p class="text-[13px] text-muted-foreground leading-relaxed mb-5">
        {{ confirmDialog.description }}
      </p>
    </BaseDialog>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue';
import { Device } from '@capacitor/device';
import { Preferences } from '@capacitor/preferences';
import { DialogTitle } from '@headlessui/vue';
import { ArrowLeftIcon, ArrowPathIcon, TrashIcon } from '@heroicons/vue/24/outline';
import { useI18n } from 'vue-i18n';
import packageJson from '../../../package.json';
import { useAppRouter } from '../../composables/useAppRouter';
import { useIsDesktop } from '../../composables/useIsDesktop';
import { useSnackbarStore } from '../../stores/snackbarStore';
import { useAppSettingsStore } from '../../stores/AppSettingsStore';
import BaseDialog, { type DialogColor } from '../../components/dialogs/BaseDialog.vue';

const { backTo } = useAppRouter();
const isDesktop = useIsDesktop();
const { t } = useI18n();
const snackbarStore = useSnackbarStore();
const appSettingsStore = useAppSettingsStore();

const version = packageJson.version;
const currentYear = new Date().getFullYear();
const platform = ref<string>('Web');

const isScrolled = ref(false);
function onScroll(e: Event) {
  isScrolled.value = (e.target as HTMLElement).scrollTop > 8;
}

// Confirm dialog state
const confirmDialog = reactive<{
  open: boolean;
  title: string;
  description: string;
  color: DialogColor;
  action: (() => Promise<void>) | null;
}>({
  open: false,
  title: '',
  description: '',
  color: 'primary',
  action: null,
});

function openConfirm(title: string, description: string, color: DialogColor, action: () => Promise<void>) {
  confirmDialog.title = title;
  confirmDialog.description = description;
  confirmDialog.color = color;
  confirmDialog.action = action;
  confirmDialog.open = true;
}

async function onConfirm() {
  confirmDialog.open = false;
  if (confirmDialog.action) await confirmDialog.action();
}

// Tap version 5x to unlock experimental features (Android Developer Options pattern)
const TAP_THRESHOLD = 5;
const TAP_TIMEOUT = 1500;
let tapCount = 0;
let tapTimer: ReturnType<typeof setTimeout> | null = null;

function onVersionTap() {
  tapCount++;
  if (tapTimer) clearTimeout(tapTimer);
  tapTimer = setTimeout(() => { tapCount = 0; }, TAP_TIMEOUT);

  const remaining = TAP_THRESHOLD - tapCount;
  if (remaining > 0 && remaining <= 3) {
    snackbarStore.addSnackbar(t('profile.about.experimental_tap_remaining', { count: remaining }), 'default', 1000);
  } else if (remaining === 0) {
    tapCount = 0;
    const unlocked = !appSettingsStore.settings.experimentalUnlocked;
    appSettingsStore.setSetting('experimentalUnlocked', unlocked);
    if (!unlocked) {
      appSettingsStore.setSetting('experimentalFeatures', false);
    }
    snackbarStore.addSnackbar(
      t(unlocked ? 'profile.about.experimental_unlocked' : 'profile.about.experimental_locked'),
      unlocked ? 'success' : 'default',
      2000
    );
  }
}

onMounted(async () => {
  try {
    const info = await Device.getInfo();
    const platformMap: Record<string, string> = {
      ios: 'iOS',
      android: 'Android',
      web: 'Web',
    };
    platform.value = platformMap[info.platform] ?? 'Web';
  } catch {
    platform.value = 'Web';
  }
});

const forceReconnect = () => {
  openConfirm(
    t('profile.about.force_reconnect'),
    t('profile.about.force_reconnect_confirm'),
    'primary',
    async () => {
      const oauthKeys = ['oauth_token', 'oauth_state', 'oauth_code_verifier', 'centers'];
      for (const key of oauthKeys) {
        await Preferences.remove({ key });
      }
      sessionStorage.clear();
      window.location.href = import.meta.env.BASE_URL;
    },
  );
};

const clearCache = () => {
  openConfirm(
    t('profile.about.clear_cache'),
    t('profile.about.clear_cache_confirm'),
    'warning',
    async () => {
      const { keys } = await Preferences.keys();
      for (const key of keys) {
        if (key.startsWith('sales_report_')) {
          await Preferences.remove({ key });
        }
      }
      await Preferences.remove({ key: 'go_manager_custom_charts' });
      snackbarStore.addSnackbar(t('profile.about.clear_cache_success'), 'success');
    },
  );
};
</script>

<style scoped>
/* Light mode */
.about-header {
  border-bottom: 1px solid transparent;
  transition: border-color 0.2s ease, backdrop-filter 0.2s ease;
}
.about-header.scrolled {
  border-bottom-color: rgba(0, 0, 0, 0.06);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}
.about-bg {
  background: linear-gradient(175deg, #FFFFFF 0%, #F0F7FA 40%, #F4F4F4 100%);
}
@media (min-width: 1280px) {
  .about-bg { background: transparent; }
}
.about-deco-top {
  background: linear-gradient(180deg, rgba(6, 200, 255, 0.09) 0%, transparent 55%);
}
.about-deco-bottom {
  background: radial-gradient(circle, rgba(6, 200, 255, 0.06) 0%, transparent 60%);
}
.about-back-btn {
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}
.about-card {
  background: #FFFFFF;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}
.about-info-divider {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}
.about-reconnect-btn {
  background: rgba(6, 200, 255, 0.08);
  color: #009FE3;
  border: 1px solid rgba(6, 200, 255, 0.15);
}
.about-reconnect-btn:active {
  background: rgba(6, 200, 255, 0.14);
}
.about-cache-btn {
  background: rgba(245, 158, 11, 0.08);
  color: #D97706;
  border: 1px solid rgba(245, 158, 11, 0.12);
}
.about-cache-btn:active {
  background: rgba(245, 158, 11, 0.14);
}
.about-scroll {
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}
.about-scroll::-webkit-scrollbar {
  display: none;
}

/* Dark mode */
:is(.dark *).about-header.scrolled {
  border-bottom-color: rgba(255, 255, 255, 0.06);
}
:is(.dark *).about-bg {
  background: linear-gradient(175deg, #1B1C1E 0%, #1e2528 40%, #1B1C1E 100%);
}
@media (min-width: 1280px) {
  :is(.dark *).about-bg { background: transparent; }
}
:is(.dark *).about-deco-top {
  background: linear-gradient(180deg, rgba(6, 200, 255, 0.15) 0%, transparent 55%);
}
:is(.dark *).about-deco-bottom {
  background: radial-gradient(circle, rgba(6, 200, 255, 0.04) 0%, transparent 50%);
}
:is(.dark *).about-back-btn {
  border: 1px solid rgba(6, 200, 255, 0.10);
  box-shadow: 0 1px 6px rgba(6, 200, 255, 0.05);
}
:is(.dark *).about-card {
  background: #1E2024;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
}
:is(.dark *).about-info-divider {
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
:is(.dark *).about-reconnect-btn {
  background: rgba(6, 200, 255, 0.12);
  border: 1px solid rgba(6, 200, 255, 0.20);
}
:is(.dark *).about-reconnect-btn:active {
  background: rgba(6, 200, 255, 0.18);
}
:is(.dark *).about-cache-btn {
  background: rgba(245, 158, 11, 0.12);
  border: 1px solid rgba(245, 158, 11, 0.20);
}
:is(.dark *).about-cache-btn:active {
  background: rgba(245, 158, 11, 0.18);
}

</style>
